'use client';

import { H2Large } from 'components/home/styleConstants';
import { CustomImage as Image } from '../..';
import { forwardRef, useRef, ReactNode, HTMLAttributes } from 'react';
import styles from './Contents.module.css';

export type WrapperProps = HTMLAttributes<HTMLDivElement> & {
  backgroundColor?: string;
};

export const Wrapper = forwardRef<HTMLDivElement, WrapperProps>(
  ({ className, backgroundColor, style, ...props }, ref) => (
    <div
      ref={ref}
      className={`${styles.wrapper} u-grid${className ? ` ${className}` : ''}`}
      style={{ backgroundColor: backgroundColor || 'transparent', ...style }}
      {...props}
    />
  ),
);
Wrapper.displayName = 'Wrapper';

// Default data for ring products
const defaultRingData = [
  {
    image: 'web_v2/uh-x-diesel/buy-page/contents/uh-diesel-ring-2.png/w_800',
    title: 'Diesel Ultrahuman Ring',
  },
  {
    image:
      'web_v2/uh-x-diesel/buy-page/contents/uh-diesel-charger-v3.png/w_800',
    title: 'Diesel Ring Charger ',
  },
  {
    image: 'web_v2/uh-x-diesel/buy-page/contents/uh-diesel-wire-2.png/w_800',
    title: 'Type-C to C Cable (1 mtr)',
  },
];

export interface ContentItem {
  image: string;
  title: ReactNode;
}

export interface ContentsProps {
  className?: string;
  htmlId?: string;
  contentData?: ContentItem[];
  backgroundColor?: string;
  contentsPadding?: string;
  $contentsBackgroundColor?: string;
  titleA?: ReactNode;
  titleB?: ReactNode;
}

export const Contents = ({
  className,
  htmlId,
  contentData,
  backgroundColor,
  contentsPadding,
  $contentsBackgroundColor,
  titleA,
  titleB,
}: ContentsProps) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Use provided data or default ring data for backward compatibility
  const data = contentData || defaultRingData;

  // Use provided titles or default ring titles
  const displayTitleA = titleA || "What's in";
  const displayTitleB = titleB || 'the box';

  return (
    <Wrapper
      className={`${styles.contentsRoot}${className ? ` ${className}` : ''}`}
      id={htmlId}
      ref={rootRef}
      aria-labelledby="contents-heading"
      backgroundColor={backgroundColor}
      data-many={data.length > 3 ? 'true' : undefined}
      style={
        {
          '--contents-bg': $contentsBackgroundColor || 'rgba(238, 238, 238, 1)',
        } as React.CSSProperties
      }
    >
      <div>
        <H2Large id="contents-heading" className="title">
          {displayTitleA} <span>{displayTitleB}</span>
        </H2Large>
        <ul
          className="contents u-hide-scrollbar"
          aria-label="Contents of the box"
          style={{
            padding: contentsPadding,
          }}
        >
          {data.map((element, index) => {
            return (
              <li key={index} className="box-content-element-wrapper">
                <div
                  className="box-content-element"
                  role="img"
                  aria-label={`Image of ${
                    element.title as unknown as string
                  }`}
                >
                  <Image
                    src={element.image}
                    alt=""
                    width="200"
                    height="200"
                    aria-hidden="true"
                  />
                </div>
                <p>{element.title}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </Wrapper>
  );
};
